/**
 * File type validation and helper utilities.
 */

export const ALLOWED_FILE_EXTENSIONS = ['.docx', '.xlsx', '.txt', '.pdf', '.ppt', '.pptx', '.csv', '.md', '.zip'];

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export const MAX_FILE_COUNT = 5;

export interface ValidationResult {
  valid: File[];
  errors: { file: string; reason: string }[];
}

/**
 * Validates an array of files against allowed extensions, size, and count limits.
 */
export function validateFiles(files: File[]): ValidationResult {
  const valid: File[] = [];
  const errors: { file: string; reason: string }[] = [];

  if (files.length > MAX_FILE_COUNT) {
    errors.push({ file: '', reason: `Maximum ${MAX_FILE_COUNT} files allowed.` });
    return { valid: [], errors };
  }

  for (const file of files) {
    if (!isAllowedFileType(file.name)) {
      errors.push({
        file: file.name,
        reason: `Unsupported file type. Allowed: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`,
      });
    } else if (file.size > MAX_FILE_SIZE_BYTES) {
      errors.push({
        file: file.name,
        reason: `File exceeds maximum size of ${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB.`,
      });
    } else {
      valid.push(file);
    }
  }

  return { valid, errors };
}

/**
 * Extracts the file extension from a filename.
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  if (parts.length < 2) return '';
  const ext = parts.pop()?.toLowerCase();
  return ext ? `.${ext}` : '';
}

/**
 * Checks if a file extension is allowed for upload.
 */
export function isAllowedFileType(filename: string): boolean {
  return ALLOWED_FILE_EXTENSIONS.includes(getFileExtension(filename));
}

/**
 * Checks if a file size is within the allowed limit.
 */
export function isAllowedFileSize(sizeBytes: number): boolean {
  return sizeBytes <= MAX_FILE_SIZE_BYTES;
}

/**
 * Returns a user-friendly validation message for a file.
 */
export function getFileValidationMessage(filename: string, sizeBytes: number): string | null {
  if (!isAllowedFileType(filename)) {
    return `File type not allowed. Supported: ${ALLOWED_FILE_EXTENSIONS.join(', ')}`;
  }
  if (!isAllowedFileSize(sizeBytes)) {
    return `File too large. Maximum size: ${MAX_FILE_SIZE_BYTES / 1024 / 1024} MB`;
  }
  return null;
}
